import { useNavigate, useParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Button, IconButton, InputBar, Spinner, Textarea } from '~/ui/index';
import styles from './TransactionForm.module.scss';
import { useTranslation } from 'react-i18next';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Tooltip from '~/components/Shared/Tooltip/Tooltip';
import ModalConfirm from '~/components/Shared/ModalConfirm';
import { TransactionTable } from './TransactionTable/TransactionTable';
import PaymentBlock from '../PaymentBlock/PaymentBlock';
import TakeawayBlock from '../TakeawayBlock/TakeawayBlock';
import ContainerBlock from '../ContainerBlock/ContainerBlock';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import CheckIcon from '../TransactionsFoodAdminBoard/TransactionsFoodAdminModal/images/check.svg?react';
import {
  checkIfUserAssetAdmin,
  checkIfUserSuperAdmin,
} from '~/utils/getUserRole';
import { SectionLink } from '~/ui/SectionLink/SectionLink';
import dayjs from 'dayjs';
import StatusBlock from '../StatusBlock/StatusBlock';
import BillBlock from '../BillBlock/BillBlock';
import { useFoodCurrency } from '~/utils/hooks/useFoodCurrency';

export interface TransactionItem {
  user_name: string;
  id: number;
  can_confirm: boolean;
  created_at: string;
  status_title: string;
  food_wares: FoodWares[];
  food_order_wares: FoodOrderWares[];
  total_sum: number;
  total_qty: string;
  food_name: string;
  comment: string;
  can_cancel: boolean;
  status?: number;
  payment_type: {
    id: number;
    name: string;
    type: string;
  };
  payment_status: string;
  takeaway: string;
  with_container: boolean;
  user_id: number;
  date_time: string;
  sent_to_avers: boolean;
  bill_id: null | number;
}

export interface FoodOrderWares {
  food_ware_id: number;
  ware_name: string;
  ware_type: string;
  weight: string;
  measure: string;
  ware_price: string;
  ware_qty: string;
  is_cancel: boolean;
  cancel_by: string;
  is_submit: boolean;
  actual_price: string;
}
export interface FoodWares {
  id: number;
  qty: number;
  total: number;
  wares: Wares;
  is_cancel: boolean;
  cancel_by: string;
  is_submit: boolean;
  parent_code: string;
  pivot: {
    is_cancel: boolean;
    is_submit: boolean;
    qty: number;
  };
  actual_price: string;
}

export interface Wares {
  wares_name: string;
  parent: string;
  weight: string;
  price: number;
  price_list: { price: string };
  photos: {
    id: number;
    full_photo_url: string;
  }[];
}

interface Props {
  transaction: TransactionItem;
}

interface TransactionParams {
  transactionID: string;
}

export const TransactionForm = ({ transaction }: Props) => {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const { transactionID, foodID } = useParams<
    keyof TransactionParams & { foodID?: string }
  >() as TransactionParams & { foodID?: string };
  const client = useQueryClient();
  const { t } = useTranslation();
  const { user } = useContext(AuthenticationContext);
  const isSuperAdmin = checkIfUserSuperAdmin(user?.data.role_name);
  const isAssetAdmin = checkIfUserAssetAdmin(user?.data.role_name);
  const currency = useFoodCurrency(foodID);

  const initialValueFoodWares = {
    id: '',
    qty: '',
    total: '',
    wares: {
      wares_name: '',
      parent: '',
      weight: '',
      price: 0,
      price_list: { price: '' },
    },
  };

  const formik = useFormik({
    initialValues: {
      user_name: '',
      id: '',
      created_at: '',
      status_title: '',
      food_wares: [initialValueFoodWares],
      total_sum: '',
      food_name: '',
      comment: '',
      food_issue: '',
    },

    onSubmit: () => {
      formik.resetForm();
    },
  });

  const handleCancelDishes = async () => {
    setIsLoading(true);
    try {
      const { data } = await instance.delete(`admin/food-order-wares`, {
        data: {
          orders_data: [
            {
              order_id: transaction?.id,
              order_ware_ids: selectedItems,
            },
          ],
        },
      });
      toastSuccess(data?.message || t('cancel_dish'));
      client.invalidateQueries(['transaction', transactionID]);
      setSelectedItems([]);
    } catch (error: any) {
      toastError(error?.response.data.error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (id: number) => {
    if (selectedItems.includes(id)) {
      const filteredFoods = selectedItems.filter(
        selectedId => selectedId !== id
      );
      setSelectedItems(filteredFoods);
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === transaction?.food_order_wares?.length) {
      setSelectedItems([]);
    } else {
      const foodIdArray = transaction?.food_order_wares?.map(
        (transaction: any) => {
          if (!transaction?.is_cancel) {
            return transaction?.food_ware_id;
          }
        }
      );
      setSelectedItems(foodIdArray);
    }
  };

  const { mutate: deleteOrder } = useMutation(
    async (id: string) => {
      setIsLoading(true);
      if (id) {
        await instance.delete(`admin/food-orders/${id}`);
      }
    },
    {
      onSuccess: () => {
        setIsLoading(false);
        client.invalidateQueries(['transactions']);
        client.invalidateQueries(['notifications-count']);
        toastSuccess(t('order_deleted'));
        navigate('/transactions');
      },
      onError: (error: any) => {
        setIsLoading(false);
        toastError(error?.response.data.error);
      },
    }
  );

  const isDisabled = !transaction?.can_cancel;

  const { mutate: approveOrder } = useMutation(
    async () => {
      setIsLoading(true);
      if (transaction.id) {
        await instance.post(`admin/food-orders-confirm/${transaction.id}`);
      }
    },
    {
      onSuccess: () => {
        setIsLoading(false);
        client.invalidateQueries(['transactions-food-point', transaction.id]);
        toastSuccess(t('order_confirmed'));
        navigate('/transactions');
      },
      onError: (error: any) => {
        setIsLoading(false);
        toastError(error?.response.data.error);
      },
    }
  );

  const { mutate: restoreOrder } = useMutation(
    async () => {
      setIsLoading(true);
      await instance.post(`admin/food-orders-restore/${transaction.id}`);
    },
    {
      onSuccess: () => {
        setIsLoading(false);
        client.invalidateQueries(['transaction', transactionID]);
        client.invalidateQueries(['transactions']);
        toastSuccess(t('order_restored'));
        navigate('/transactions');
      },
      onError: (error: any) => {
        setIsLoading(false);
        toastError(error?.response?.data?.error);
      },
    }
  );

  const handleSendOrder = async () => {
    try {
      await instance.post('admin/send-order-to-box-office', {
        order_ids: [Number(transactionID)],
      });
      setSelectedItems([]);
      toastSuccess(t('orders_shipped_successfully'));
      client.invalidateQueries(['transactions']);
      client.invalidateQueries(['transaction', transactionID]);
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    if (!transaction) return;

    formik.setValues({
      user_name: transaction?.user_name,
      id: transaction?.id.toString(),
      created_at: transaction?.created_at,
      status_title: transaction?.status_title,
      food_wares: transaction?.food_wares?.map(foodWare => ({
        id: foodWare.id.toString(),
        qty: foodWare.qty.toString(),
        total: foodWare.total.toString(),
        wares: {
          wares_name: foodWare.wares.wares_name,
          parent: foodWare.wares.parent,
          weight: foodWare.wares.weight,
          price: foodWare.wares.price,
          price_list: { price: foodWare?.wares?.price_list?.price },
        },
      })),
      total_sum: transaction?.total_sum.toString(),
      food_name: transaction?.food_name,
      comment: transaction?.comment,
      food_issue: transaction?.date_time
        ? dayjs(transaction?.date_time).format('DD.MM.YYYY HH:mm')
        : t('issue_date_missing'),
    });
  }, [transaction]);

  return (
    <div className={styles.formWrapper}>
      <div className={styles.header}>
        {transaction?.payment_type ? (
          <PaymentBlock
            name={transaction?.payment_type.name}
            type={transaction?.payment_type.type}
            status_type={transaction?.payment_status}
          />
        ) : (
          ''
        )}
        <TakeawayBlock isInCafe={!transaction?.takeaway} />
        <ContainerBlock isWithContainer={transaction?.with_container} />
        <StatusBlock isSent={transaction?.sent_to_avers} />
        <BillBlock bill_id={transaction?.bill_id} />
      </div>
      <form
        className={styles.form}
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        <div className={styles.formTop}>
          <div className={styles.infoWrapper}>
            <InputBar
              name="user_name"
              value={formik.values.user_name}
              disabled={true}
              label={t('employee')}
              sectionLink={
                <SectionLink
                  title={t('to_contact')}
                  link={`/user/${transaction?.user_id}`}
                />
              }
            />

            <InputBar
              name="id"
              value={formik.values.id}
              disabled={true}
              label={t('order_number')}
            />
            <InputBar
              name="created_at"
              value={formik.values.created_at}
              disabled={true}
              label={t('order_date')}
            />
          </div>
          <div className={styles.foodInfoWrapper}>
            <InputBar
              name="food_name"
              value={formik.values.food_name}
              disabled={true}
              label={t('food_object')}
            />
            <InputBar
              name="food_issue"
              value={formik.values.food_issue}
              disabled={true}
              label={t('food_issue')}
            />
          </div>
          <div className={styles.input__label}>
            <div className={styles.input__head}>
              <span className={styles.input__text}>{t('compound')}</span>
              <div className={styles.button__wrapper}>
                {(isAssetAdmin || isSuperAdmin) && transaction?.can_confirm && (
                  <IconButton
                    type="button"
                    onClick={approveOrder}
                    className={styles.approveButton}
                  >
                    <CheckIcon />
                    {t('approve_order')}
                  </IconButton>
                )}
                {!!selectedItems.length && (
                  <div className={styles.cancelButtonWrapper}>
                    <ModalConfirm
                      title={`${t('cancel_dishes_confirmation')}`}
                      onConfirm={handleCancelDishes}
                    >
                      {open => (
                        <Button
                          type="button"
                          text={t('cancel')}
                          color="red"
                          onClick={open}
                          className={styles.deleteButton}
                          disabled={isDisabled}
                        />
                      )}
                    </ModalConfirm>
                  </div>
                )}
                {transaction?.status !== 3 ? (
                  <>
                    {isDisabled ? (
                      <div className={styles.cancelButtonWrapper}>
                        <Tooltip
                          tooltipStyle={styles.transactionTooltip}
                          title={t('canceled_tooltip')}
                        >
                          <Button
                            color="red"
                            text={t('cancel_order')}
                            onClick={() => deleteOrder(transactionID)}
                            disabled={isDisabled}
                            className={styles.deleteButton}
                          />
                        </Tooltip>
                      </div>
                    ) : (
                      <div className={styles.cancelButtonWrapper}>
                        <ModalConfirm
                          title={`${t(
                            'cancel_order_confirmation'
                          )} №${transactionID}?`}
                          onConfirm={() => deleteOrder(transactionID)}
                        >
                          {open => (
                            <Button
                              color="red"
                              text={t('cancel_order')}
                              onClick={open}
                              disabled={isLoading}
                              className={styles.deleteButton}
                            />
                          )}
                        </ModalConfirm>
                      </div>
                    )}
                  </>
                ) : (
                  (isSuperAdmin || isAssetAdmin) && (
                    <div className={styles.cancelButtonWrapper}>
                      {transaction?.sent_to_avers || transaction?.bill_id ? (
                        <Tooltip
                          tooltipStyle={styles.transactionTooltip}
                          title={t('restore_order_disabled_tooltip')}
                        >
                          <Button
                            text={t('restore_order')}
                            disabled={true}
                            className={styles.deleteButton}
                          />
                        </Tooltip>
                      ) : (
                        <ModalConfirm
                          title={`${t('restore_order_confirmation')} №${transaction?.id}?`}
                          onConfirm={restoreOrder}
                        >
                          {open => (
                            <Button
                              text={t('restore_order')}
                              onClick={open}
                              disabled={isLoading}
                              className={styles.deleteButton}
                            />
                          )}
                        </ModalConfirm>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
            <div className={styles.transactionWrapper}>
              {!transaction?.food_wares && isLoading ? (
                <div className={styles.loader}>
                  <Spinner />
                </div>
              ) : (
                ''
              )}
              <TransactionTable
                isAllCancelled={transaction?.status === 3}
                selectedItems={selectedItems}
                onCheckboxHeadPress={handleSelectAll}
                onCheckboxPress={handleSelect}
                order={transaction?.food_order_wares}
                totalSum={transaction?.total_sum}
                totalAmount={transaction?.total_qty}
                currency={currency}
              />
            </div>
          </div>
          {/* <div> */}
          <Button
            text={t('send_order')}
            className={styles.sendBtn}
            onClick={handleSendOrder}
          />
          {/* </div> */}
          <Textarea
            name="comment"
            rows={4}
            star={false}
            maxLength={1000}
            label={t('comment')}
            disabled
            value={transaction?.comment || t('no_comment')}
          />
        </div>
        <div className={styles.submitBtnWrapper}>
          <Button
            type="submit"
            text={t('back_to_list')}
            className={styles.btn}
            onClick={goBack}
            disabled={false}
          />
        </div>
      </form>
    </div>
  );
};
